import { useId } from "react";
import { Icon } from "./utils/Status.helpers";
import { STATUS_CONFIG, STATUS_SIZE } from "./config/Status.config";
import { StatusOverflow } from "./common";
import "./Status.scss";

// ============================================================
//  StatusChip — square tag, icon-forward, no dot
//  Props: status, size ("sm"|"md"|"lg"), showIcon,
//         icon (any Iconify icon name — overrides the default)
// ============================================================
export const StatusChip = ({
    status,
    size = STATUS_SIZE.MD,
    showIcon = false,
    iconName,
    tooltipId,
    maxWidth,
    onClick,
    DEFAULT,
    className = "",
    style = {},
    showDot = true,
    ...otherIconConfig
}) => {
    const generatedId = useId().replace(/:/g, "");
    const targetId = tooltipId || `status-chip-${generatedId}`;

    const uppercaseNaming = status?.replace(/\s+/g, "").toUpperCase();
    const cfg = STATUS_CONFIG[uppercaseNaming] || DEFAULT || otherIconConfig.DEFAULT;
    if (!cfg) return null;

    // icon prop overrides STATUS_CONFIG default; falls back to config icon
    const resolvedIcon = iconName || cfg.iconName;

    const s = {
        xs: { fs: "10px", px: "8px", py: "3px", gap: "4px", dot: 5, iconSize: 11 },
        sm: { fs: "11px", px: "10px", py: "4px", gap: "5px", dot: 6, iconSize: 12 },
        md: { fs: "12px", px: "12px", py: "5px", gap: "6px", dot: 7, iconSize: 13 },
        lg: { fs: "13px", px: "14px", py: "6px", gap: "7px", dot: 8, iconSize: 14 },
        xl: { fs: "14px", px: "16px", py: "7px", gap: "8px", dot: 9, iconSize: 15 },
    }[size] || { fs: "12px", px: "12px", py: "5px", gap: "6px", dot: 7, iconSize: 13 };

    return (
        <span
            id={targetId}
            className={`status-chip ${className}`}
            onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: cfg.color,
                background: cfg.bg,
                border: `1.5px solid ${cfg.border}`,
                lineHeight: 1.5,
                maxWidth: maxWidth || "100%",
                minWidth: 0,
                boxSizing: "border-box",
                cursor: onClick ? "pointer" : "default",
                "--status-fs": s.fs,
                "--status-px": s.px,
                "--status-py": s.py,
                "--status-gap": s.gap,
                ...style,
            }}
        >
            {/* dot */}
            {showDot && (
                <span
                    className={cfg.pulse ? "ss-pulse" : ""}
                    style={{
                        width: s.dot,
                        height: s.dot,
                        borderRadius: "50%",
                        background: cfg.dot,
                        flexShrink: 0,
                        display: "block",
                        color: cfg.dot,
                    }}
                />
            )}
            {/* icon: custom prop → config default → nothing if showIcon=false */}
            {showIcon && (
                <Icon
                    iconName={resolvedIcon}
                    size={s.iconSize}
                    style={{ flexShrink: 0 }}
                    {...otherIconConfig}
                />
            )}

            {/* label */}
            <StatusOverflow label={cfg.label} targetId={targetId} />
        </span>
    );
};